<script setup>
import axios from 'axios';
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
} from 'bootstrap-vue-next';
import { computed, onMounted, ref, watch } from 'vue';

import ErrorState from '~/common/components/ErrorState.vue';
import { daysAgo } from '~/common/DateUtils';
import { parseList, serializeList } from '~/common/QueryParams';

const props = defineProps({
  initialFilters: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['filter']);

const hierarchyData = ref([]);
const hierarchyError = ref(null);

const period = ref(props.initialFilters.period || 'month');
const groupBy = ref(props.initialFilters.groupBy || 'section');
const chartType = ref(props.initialFilters.chartType || 'line');
const fromDate = ref(props.initialFilters.fromDate || daysAgo(365));
const toDate = ref(props.initialFilters.toDate || '');
const selectedGroups = ref(parseList(props.initialFilters.groups));
const selectedProjects = ref(parseList(props.initialFilters.projects));
const selectedSections = ref(parseList(props.initialFilters.sections));
const selectedStages = ref(parseList(props.initialFilters.stages));
const selectedLabels = ref(parseList(props.initialFilters.labels));
const username = ref(props.initialFilters.username || '');
const communityOnly = ref(props.initialFilters.communityOnly !== 'false');

const sectionOptions = computed(() => {
  const sections = hierarchyData.value.map(group => group.section).filter(Boolean);

  return [...new Set(sections)].sort();
});

const filteredStageOptions = computed(() => {
  let entries = hierarchyData.value;

  if (selectedSections.value.length) {
    entries = entries.filter(v => selectedSections.value.includes(v.section));
  }

  const stages = entries.map(v => v.stage).filter(Boolean);

  return [...new Set(stages)].sort();
});

const filteredGroupSelectOptions = computed(() => {
  let entries = hierarchyData.value;

  if (selectedSections.value.length) {
    entries = entries.filter(group => selectedSections.value.includes(group.section));
  }

  if (selectedStages.value.length) {
    entries = entries.filter(group => selectedStages.value.includes(group.stage));
  }

  return entries.map(({ label, name }) => ({
    value: label,
    text: name || label.replace('group::', ''),
  })).sort((a, b) => a.text.localeCompare(b.text));
});

const updateSelectedLabels = (event) => {
  selectedLabels.value = event.target.value.split(',').map(s => s.trim()).filter(Boolean);
};

const buildParams = () => ({
  'from_date': fromDate.value,
  'to_date': toDate.value || undefined,
  'period': period.value,
  'group_by': groupBy.value,
  'groups[]': selectedGroups.value.length ? selectedGroups.value : undefined,
  'projects[]': selectedProjects.value.length ? selectedProjects.value : undefined,
  'sections[]': selectedSections.value.length ? selectedSections.value : undefined,
  'stages[]': selectedStages.value.length ? selectedStages.value : undefined,
  'labels[]': selectedLabels.value.length ? selectedLabels.value : undefined,
  'username': username.value || undefined,
  'community_only': communityOnly.value || undefined,
});

const buildQueryString = () => ({
  period: period.value,
  groupBy: groupBy.value,
  chartType: chartType.value,
  fromDate: fromDate.value,
  communityOnly: communityOnly.value.toString(),
  toDate: toDate.value || undefined,
  groups: serializeList(selectedGroups.value),
  projects: serializeList(selectedProjects.value),
  sections: serializeList(selectedSections.value),
  stages: serializeList(selectedStages.value),
  labels: serializeList(selectedLabels.value),
  username: username.value || undefined,
});

const updateSelectedProjects = (event) => {
  selectedProjects.value = event.target.value.split(',').map(s => s.trim()).filter(Boolean);
};

const applyFilters = () => {
  emit('filter', { params: buildParams(), query: buildQueryString() });
};

const fetchFilterOptions = async () => {
  try {
    const response = await axios.get('/api/v1/insights/hierarchy');
    hierarchyData.value = response.data;
  } catch (e) {
    hierarchyError.value = e;
  }
};

watch(selectedSections, () => {
  const validStages = new Set(filteredStageOptions.value);
  selectedStages.value = selectedStages.value.filter(s => validStages.has(s));
});

watch(selectedStages, () => {
  const validGroups = new Set(filteredGroupSelectOptions.value.map(o => o.value));
  selectedGroups.value = selectedGroups.value.filter(g => validGroups.has(g));
});

watch(chartType, () => {
  emit('filter', { params: buildParams(), query: buildQueryString() });
});

defineExpose({
  buildParams,
  buildQueryString,
  chartType,
  hierarchyData,
  selectedSections,
  selectedStages,
  selectedGroups,
});

onMounted(() => {
  fetchFilterOptions();
  applyFilters();
});
</script>

<template>
  <div>
    <ErrorState v-if="hierarchyError" />

    <BCard class="mb-4">
      <BForm @submit.prevent="applyFilters">
        <BRow class="gy-3 mb-3">
          <BCol
            xs="12"
            md="6"
            lg="3"
          >
            <BFormGroup
              label="View by"
              label-for="group-by"
            >
              <BFormSelect
                id="group-by"
                v-model="groupBy"
              >
                <option value="section">Section</option>
                <option value="stage">Stage</option>
                <option value="group">Group</option>
                <option value="project">Project</option>
              </BFormSelect>
            </BFormGroup>
          </BCol>
          <BCol
            xs="12"
            md="6"
            lg="3"
          >
            <BFormGroup
              label="Period"
              label-for="period"
            >
              <BFormSelect
                id="period"
                v-model="period"
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </BFormSelect>
            </BFormGroup>
          </BCol>
          <BCol
            xs="12"
            md="6"
            lg="3"
          >
            <BFormGroup
              label="From date"
              label-for="from-date"
            >
              <BFormInput
                id="from-date"
                v-model="fromDate"
                type="date"
              />
            </BFormGroup>
          </BCol>
          <BCol
            xs="12"
            md="6"
            lg="3"
          >
            <BFormGroup
              label="To date"
              label-for="to-date"
            >
              <BFormInput
                id="to-date"
                v-model="toDate"
                type="date"
              />
            </BFormGroup>
          </BCol>
        </BRow>

        <BRow class="gy-3 mb-3">
          <BCol
            xs="12"
            md="6"
            lg="3"
          >
            <BFormGroup
              label="Section"
              label-for="sections"
            >
              <BFormSelect
                id="sections"
                v-model="selectedSections"
                :options="sectionOptions"
                :select-size="5"
                multiple
              />
            </BFormGroup>
          </BCol>
          <BCol
            xs="12"
            md="6"
            lg="3"
          >
            <BFormGroup
              label="Stage"
              label-for="stages"
            >
              <BFormSelect
                id="stages"
                v-model="selectedStages"
                :options="filteredStageOptions"
                :select-size="5"
                multiple
              />
            </BFormGroup>
          </BCol>
          <BCol
            xs="12"
            md="6"
            lg="3"
          >
            <BFormGroup
              label="Groups"
              label-for="groups"
            >
              <BFormSelect
                id="groups"
                v-model="selectedGroups"
                :options="filteredGroupSelectOptions"
                :select-size="5"
                multiple
              />
            </BFormGroup>
          </BCol>
          <BCol
            xs="12"
            md="6"
            lg="3"
          >
            <BFormGroup
              label="Projects (comma-separated paths)"
              label-for="projects"
            >
              <BFormInput
                id="projects"
                :model-value="selectedProjects.join(', ')"
                placeholder="e.g. gitlab-org/gitlab, gitlab-org/gitaly"
                @change="updateSelectedProjects"
              />
            </BFormGroup>
            <BFormGroup
              label="Labels (comma-separated)"
              label-for="labels"
              class="mt-2"
            >
              <BFormInput
                id="labels"
                :model-value="selectedLabels.join(', ')"
                placeholder="e.g. Hackathon, Community contribution"
                @change="updateSelectedLabels"
              />
            </BFormGroup>
          </BCol>
        </BRow>

        <BRow class="gy-3 mb-3 align-items-end">
          <BCol
            xs="12"
            md="6"
            lg="3"
          >
            <BFormGroup
              label="Username"
              label-for="username"
            >
              <BFormInput
                id="username"
                v-model="username"
                placeholder="Filter by username"
              />
            </BFormGroup>
          </BCol>
          <BCol
            xs="12"
            md="6"
            lg="3"
          >
            <BFormGroup
              label="Chart Type"
              label-for="chart-type"
            >
              <BFormSelect
                id="chart-type"
                v-model="chartType"
              >
                <option value="line">Line</option>
                <option value="bar">Bar</option>
              </BFormSelect>
            </BFormGroup>
          </BCol>
          <BCol
            xs="12"
            md="6"
            lg="3"
          >
            <BFormCheckbox
              v-model="communityOnly"
              :unchecked-value="false"
              class="mt-4"
            >
              Community only
            </BFormCheckbox>
          </BCol>
          <BCol
            xs="12"
            md="6"
            lg="3"
            class="d-flex align-items-end justify-content-end"
          >
            <BButton
              variant="primary"
              type="submit"
              class="mb-3"
            >
              Apply
            </BButton>
          </BCol>
        </BRow>
      </BForm>
    </BCard>
  </div>
</template>
